































































































































































import {Component, Prop, Vue} from "vue-property-decorator";
import CountriesModal from "./CountriesModal.vue";
import {CustomerBalance, Country} from "@/interfaces/entities/CustomerProfile";
import {
    isUnlimitedBalance,
    isCreditsBasedBalance,
    isDataExhaustedBalance,
    isValidityExpiredBalance,
    computeValidityElapsedPercent,
    dataRemainingPercent
} from "@/util/balancePlanStatus";
import {convertSize} from "@/util/dataSize";
import {accentAlpha, accentShade} from "@/util/accentColor";

// Which balance metric the ring/usage panel renders.
type PackageMode = "credits" | "unlimited" | "expired" | "exhausted" | "standard";

const WARNING_COLOR = "#D97706";
const LOW_DATA_THRESHOLD_PERCENT = 20;
const CRITICAL_COLOR = "#DC2626";
const NEUTRAL_RING_COLOR = "#D1D5DB";
const VALIDITY_WARNING_THRESHOLD_PERCENT = 80;
// The teal tints the design originally hardcoded, kept as fallbacks for a non-hex accent colour.
const DEFAULT_NOTICE_SURFACE = "#E6F2F1";
const DEFAULT_NOTICE_BORDER = "#C2DDD9";
const DEFAULT_NOTICE_ACCENT = "#0B7A73";
const DEFAULT_NOTICE_TEXT = "#0B5C57";
const DAY_MS = 24 * 60 * 60 * 1000;
const RING_RADIUS = 64.5;

@Component({
    components: {CountriesModal}
})
export default class ActivePackageCard extends Vue {
    @Prop({required: true}) balance!: CustomerBalance;
    @Prop({required: true}) iccid!: string;
    @Prop({required: true}) accentColor!: string;
    @Prop({type: Boolean, default: false}) refreshing!: boolean;

    private showCountriesModal: boolean = false;

    // -- Package basics --

    get packageName(): string {
        return this.balance.balance.name;
    }

    get packageSize(): number {
        return this.balance.balance.size.sizeValue;
    }

    get packageSizeUnit(): string {
        return this.balance.balance.size.sizeUnit;
    }

    get validitySize(): number {
        return this.balance.balance.validitySize;
    }

    get validityUnit(): string {
        return this.balance.balance.validityUnit;
    }

    // Reported in `availableBalance.sizeUnit`, which is NOT necessarily `packageSizeUnit` -
    // the server drops to MB below 1 GB. Always pair this with `remainingUnit` when displaying,
    // and go through convertSize/dataRemainingPercent before comparing it against packageSize.
    get remainingValue(): number {
        return this.balance.balance.availableBalance.sizeValue;
    }

    get remainingUnit(): string {
        return this.balance.balance.availableBalance.sizeUnit;
    }

    // Rendered next to packageSize ("Used 4.2 of 5 GB"), so it has to be expressed in the
    // package's own unit rather than whatever unit the remaining balance came back in.
    get usedValue(): number {
        const remainingInPackageUnit = convertSize(this.remainingValue, this.remainingUnit, this.packageSizeUnit);
        const remaining = remainingInPackageUnit ?? this.remainingValue;
        return Math.max(0, Math.round((this.packageSize - remaining) * 10) / 10);
    }

    get remainingPercent(): number {
        return dataRemainingPercent(this.balance);
    }

    // -- Package mode: data cap vs unlimited vs credits --
    // Shared with CustomerProfilePage via util/balancePlanStatus, so the two
    // views can't drift on what "unlimited" / "credits-based" means.

    get isUnlimited(): boolean {
        return isUnlimitedBalance(this.balance);
    }

    get isCreditsBased(): boolean {
        return isCreditsBasedBalance(this.balance);
    }

    // Single source of truth for which usage-panel variant to render.
    // Validity expiry trumps data exhaustion, matching CustomerProfilePage's displayStatus priority.
    get packageMode(): PackageMode {
        if (this.isCreditsBased) return "credits";
        if (this.isUnlimited) return "unlimited";
        if (this.isValidityExpired) return "expired";
        if (this.isDataExhausted) return "exhausted";
        return "standard";
    }

    // Deliberately local, not the shared `fiatFormat` filter - that one is
    // locked to ISO-code display ("USD 6.40"); this screen needs a symbol ("$6.40").
    private formatMoney(value: number, currencyCode: string): string {
        return new Intl.NumberFormat(this.$i18n.locale, {
            style: "currency",
            currency: currencyCode || "USD",
            minimumFractionDigits: 2
        }).format(value);
    }

    get ringValue(): string {
        // Validity trumps whatever the API still reports as available - a plan
        // past its expiry date reads as 0 left even if a stale balance lingers.
        if (this.packageMode === "expired") return "0";
        switch (this.packageMode) {
            case "credits": return this.formatMoney(Math.max(0, this.remainingValue), this.packageSizeUnit);
            case "unlimited": return String(this.daysLeft);
            default: return String(Math.max(0, this.remainingValue));
        }
    }

    get ringLabel(): string {
        switch (this.packageMode) {
            case "credits": return this.$t("esimDataPlan.creditsRemaining") as string;
            case "unlimited": return this.$t("esimDataPlan.daysLeft") as string;
            // The unit travels with the value - a nearly-empty GB package reports MB.
            default: return this.$t("esimDataPlan.dataLeft", {unit: this.remainingUnit}) as string;
        }
    }

    // -- Ring arc: percentage of whichever resource is scarce for this mode --

    get ringCircumference(): number {
        return 2 * Math.PI * RING_RADIUS;
    }

    // Unlimited plans have no data cap, so the ring tracks days left instead;
    // any other mode running out (data exhausted or validity expired) reads as empty.
    get ringPercent(): number {
        if (this.packageMode === "unlimited") {
            return Math.max(0, Math.min(100, 100 - this.validityElapsedPercent));
        }
        if (this.isCriticalState) return 0;
        return Math.max(0, Math.min(100, this.remainingPercent));
    }

    get ringDashOffset(): number {
        return this.ringCircumference * (1 - this.ringPercent / 100);
    }

    get ringColor(): string {
        return this.ringPercent <= 0 ? NEUTRAL_RING_COLOR : this.severityColor;
    }

    // -- Unlimited: validity days + daily full-speed quota --

    get daysLeft(): number {
        const remainingMs = new Date(this.balance.balance.expiresAt).getTime() - Date.now();
        return Math.max(0, Math.ceil(remainingMs / DAY_MS));
    }

    get fullSpeedTotalGb(): number {
        return this.balance.balance.extendedDataPolicy?.totalHighSpeedVolume.sizeValue ?? 0;
    }

    get fullSpeedUnit(): string {
        return this.balance.balance.extendedDataPolicy?.totalHighSpeedVolume.sizeUnit ?? "";
    }

    get fullSpeedUsedGb(): number {
        const policy = this.balance.balance.extendedDataPolicy;
        if (!policy) return 0;
        return Math.max(0, Math.round((policy.totalHighSpeedVolume.sizeValue - policy.remainingHighSpeedVolume.sizeValue) * 10) / 10);
    }

    get fullSpeedUsedPercent(): number {
        return this.fullSpeedTotalGb > 0 ? Math.max(0, Math.min(100, (this.fullSpeedUsedGb / this.fullSpeedTotalGb) * 100)) : 0;
    }

    // -- Severity coloring (ring + expiry bar) --

    get isDataExhausted(): boolean {
        return isDataExhaustedBalance(this.balance);
    }

    get isLowData(): boolean {
        return !this.isUnlimited && this.remainingPercent < LOW_DATA_THRESHOLD_PERCENT;
    }

    get isValidityExpired(): boolean {
        return isValidityExpiredBalance(this.balance);
    }

    get isValidityWarning(): boolean {
        return !this.isCreditsBased && !this.isValidityExpired && this.validityElapsedPercent >= VALIDITY_WARNING_THRESHOLD_PERCENT;
    }

    // Whichever axis (data or validity) runs out first drags the other's
    // color along too, instead of leaving a partially-green pair that still looks fine.
    get isCriticalState(): boolean {
        return this.isDataExhausted || this.isValidityExpired;
    }

    get isWarningState(): boolean {
        return !this.isCriticalState && (this.isLowData || this.isValidityWarning);
    }

    get severityColor(): string {
        if (this.isCriticalState) return CRITICAL_COLOR;
        if (this.isWarningState) return WARNING_COLOR;
        return this.accentColor;
    }

    get validityElapsedPercent(): number {
        return computeValidityElapsedPercent(this.balance);
    }

    // Forced to read as fully used once data runs out too - the plan's dead either way.
    get displayedValidityElapsedPercent(): number {
        return this.isCriticalState ? 100 : this.validityElapsedPercent;
    }

    get expiryStatusColor(): string {
        return this.severityColor;
    }

    // Custom properties rather than four separate :style bindings - they inherit, so the icon
    // and text inside the notice pick them up from the container.
    get infoNoticeStyle(): Record<string, string> {
        return {
            "--notice-surface": accentAlpha(this.accentColor, 0.1, DEFAULT_NOTICE_SURFACE),
            "--notice-border": accentAlpha(this.accentColor, 0.25, DEFAULT_NOTICE_BORDER),
            "--notice-accent": this.accentColor || DEFAULT_NOTICE_ACCENT,
            "--notice-text": accentShade(this.accentColor, 0.75, DEFAULT_NOTICE_TEXT)
        };
    }

    // -- Formatted display values --

    get expiresAtFormatted(): string {
        const balance = this.balance.balance;
        if (!balance.activatedAt) {
            const deadline = this.$options.filters?.dateFormat(balance.expiresAt, "DD MMM YYYY") as string;
            return this.$t("esimDataPlan.installUntil", {date: deadline}) as string;
        }
        return this.$options.filters?.dateFormat(balance.expiresAt, "DD MMM YYYY, HH:mm") as string;
    }

    // expiresAtFormatted reads "Install until <date>" while a plan is still uninstalled, which
    // makes no sense once it has expired - the window is gone either way, so state the date plainly.
    get expiredAtFormatted(): string {
        return this.$options.filters?.dateFormat(this.balance.balance.expiresAt, "DD MMM YYYY, HH:mm") as string;
    }

    get purchasedAtFormatted(): string {
        return this.$options.filters?.dateFormat(this.balance.salesDate, "DD MMM YYYY") as string;
    }

    get activatedAtFormatted(): string {
        const activatedAt = this.balance.balance.activatedAt;
        return activatedAt ? (this.$options.filters?.dateFormat(activatedAt, "DD MMM YYYY") as string) : "---";
    }

    // -- Countries --

    get countryCount(): number {
        return this.balance.includedCountries.length;
    }

    get includedCountries(): Array<Country> {
        return this.balance.includedCountries;
    }

    get regionLabel(): string {
        return this.$options.filters?.countryName
            ? (this.$options.filters.countryName(this.balance.balance.countrySet) as string)
            : this.balance.balance.countrySet;
    }
}
